import { LanguageType, Location, TransportModes } from '@nx-smartmonkey/shared/domain';

import { AnalyticsSummary } from '../domain/analytics/AnalyticsSummary';
import { DriverCredentials } from '../domain/credentials/DriverCredentials';
import { DriversProject } from '../domain/driversProject/DriversProject';
import { Vehicle } from '../domain/vehicle/Vehicle';
import { vehicleRepository } from '../repositories/vehicle';
import { VehicleRepository } from '../repositories/vehicle/VehicleRepository';
import { sentryService, SentryService } from './SentryService';
import { sessionService, SessionService } from './SessionService';
import { webSocketsService } from './WebSocketsService';

class VehicleService {
  constructor(
    private vehicleRepository: VehicleRepository,
    private sessionService: SessionService,
    private sentryService: SentryService
  ) {}

  async getVehicle(): Promise<{ vehicle: Vehicle; project: DriversProject }> {
    const vehicleData = await this.vehicleRepository.getVehicle();

    webSocketsService.connect((await this.sessionService.getToken())!);

    // Setting sentry user (authenticated with phone)
    this.sentryService.setUser({
      Authenticated: true,
      id: vehicleData.vehicle.id,
      VehicleId: vehicleData.vehicle.id,
      OrganizationId: vehicleData.vehicle.organization_id,
      ProjectId: vehicleData.vehicle.project_id,
      Phone: vehicleData.vehicle.phone,
    });

    return vehicleData;
  }

  async deleteAccount(): Promise<void> {
    await this.vehicleRepository.deleteAccount();
  }

  async retrieveCredentials(): Promise<DriverCredentials> {
    return this.vehicleRepository.retrieveCredentials();
  }

  updateRegisterLocation({ lat, lng, lang }: { lat: number; lng: number; lang?: LanguageType }): Promise<Location> {
    return this.vehicleRepository.updateRegisterLocation({ lat, lng, lang });
  }

  updateProject({
    stop_duration,
    distance_unit,
    use_traffic,
    stop_grouping,
    transport_mode,
  }: {
    stop_duration?: number;
    distance_unit?: string;
    use_traffic?: boolean;
    stop_grouping?: boolean;
    transport_mode?: TransportModes;
  }): Promise<DriversProject> {
    return this.vehicleRepository.updateProject({
      stop_duration,
      distance_unit,
      use_traffic,
      stop_grouping,
      transport_mode,
    });
  }

  updateVehicle({ vehicle }: { vehicle: Vehicle }): Promise<Vehicle> {
    return this.vehicleRepository.updateVehicle({
      vehicle,
    });
  }

  retrieveAnalyticsSummary({ startDate, endDate }: { startDate: Date; endDate: Date }): Promise<AnalyticsSummary> {
    return this.vehicleRepository.retrieveAnalyticsSummary({ startDate, endDate });
  }
}

export const vehicleService = new VehicleService(vehicleRepository, sessionService, sentryService);
